import type { FitScoreLevel } from './product-fit-score-service-utils'

// Categorias que o backend envia no campo `category` do fit-score. Espelha os nomes
// de `Category.Enum` (domain/models/content/category). Vem `null` na resposta legada,
// anterior à expansão para idiomas (TS-8852).
export type FitScoreCategory =
  | 'COURSE'
  | 'LANGUAGE'
  | 'UNDERGRADUATE'
  | 'GRADUATE'
  | 'MENTORING'
  | 'TECHNICAL'
  | 'HIGH_SCHOOL'
  | 'EXTENSION'

// Skills avaliadas em educação/formação
export type FitScoreEducationSkillKey = 'professionalRole' | 'seniority' | 'language'
// Skills avaliadas em idiomas — o cálculo é por oferta, não por produto
export type FitScoreLanguageSkillKey = 'dedication' | 'modal' | 'classType'

export type FitScoreSkillKey = FitScoreEducationSkillKey | FitScoreLanguageSkillKey

export interface FitScoreSkillCompatibility {
  compatibility: FitScoreLevel
}

export interface FitScoreProfessionalRoleCompatibility extends FitScoreSkillCompatibility {
  currentProfessionalRole: string
}

export interface FitScoreSeniorityCompatibility extends FitScoreSkillCompatibility {
  requiredSeniority: string
  currentSeniority: string
}

// Par idioma + proficiência usado pelo backend para registrar tanto o idioma do curso
// quanto o do usuário considerados no cálculo de compatibilidade.
// `proficiency` vem como string em PT ("nativo", "básico", "avançado") por enquanto —
// quando o backend mudar pra código (NATIVE, BASIC, ADVANCED), traduzir via i18n.
export interface FitScoreLanguageEntry {
  language: string
  proficiency: string
}

export interface FitScoreLanguageCompatibility extends FitScoreSkillCompatibility {
  requiredLanguage: {
    caption: string[]
    main: string[]
    // Idioma do curso usado no cálculo. Vira null quando o usuário não tem match com nenhum
    // dos idiomas do curso; a frase da tela então se monta a partir de `main`
    considered: FitScoreLanguageEntry | null
  }
  currentLanguage: {
    userList: FitScoreLanguageEntry[]
    // Idioma do usuário usado no cálculo (sempre populado — vem o idioma do user
    // mesmo quando não bate com o curso)
    considered: FitScoreLanguageEntry
  }
}

// Dedicação semanal que a oferta exige × a que o usuário declarou em learning-preferences.
// Os dois valores em HORAS, apesar do nome — o contrato deixou a unidade fora do campo.
export interface FitScoreDedicationCompatibility extends FitScoreSkillCompatibility {
  requiredWeeklyDedication: number
  currentWeeklyDedication: number | null
}

// Modalidade da oferta (LIVE_STREAMING / ON_DEMAND_STREAMING / ONSITE) × as preferências
// do usuário. `currentPreferences` é lista porque o usuário escolhe até 2 formatos;
// `considered` é o que o backend usou no cálculo.
export interface FitScoreModalCompatibility extends FitScoreSkillCompatibility {
  requiredModal: string
  currentPreferences: string[]
  considered: string | null
}

// Tipo de turma da oferta (INDIVIDUAL / GROUP) × preferência do usuário.
// `currentClassType` vem null quando o usuário ainda não informou.
export interface FitScoreClassTypeCompatibility extends FitScoreSkillCompatibility {
  requiredClassType: string
  currentClassType: string | null
}

/**
 * As seis skills possíveis. O backend popula apenas o grupo da categoria e manda
 * `null` no outro: educação/formação preenche professionalRole/seniority/language,
 * idiomas preenche dedication/modal/classType.
 */
export interface FitScoreSkillsCompatibility {
  professionalRole?: FitScoreProfessionalRoleCompatibility | null
  seniority?: FitScoreSeniorityCompatibility | null
  language?: FitScoreLanguageCompatibility | null
  dedication?: FitScoreDedicationCompatibility | null
  modal?: FitScoreModalCompatibility | null
  classType?: FitScoreClassTypeCompatibility | null
}

/**
 * Situação de elegibilidade do usuário para a oferta avaliada.
 *
 * Convive com `approved` em vez de substituí-lo: a promoção de LOW/MEDIUM para HIGH na UI
 * continua saindo de `approved`. Hoje nenhum componente reage a este campo — `APPROVAL_REQUIRED`
 * e `NO_APPROVAL_REQUIRED` são redundantes com `showRecommendations`, e `BLOCKED` se
 * sobrepõe a `restricted`/`restrictionType` de /v2/user/content/product/{id}/enrollment, que
 * segue sendo o canal que impede a matrícula.
 */
export type FitScoreEligibilityStatus = 'BLOCKED' | 'APPROVAL_REQUIRED' | 'NO_APPROVAL_REQUIRED'

/**
 * Oferta que o backend usou no cálculo.
 *
 * Quando a requisição não manda `offer_id`, o backend avalia todas as ofertas do produto e
 * devolve aqui a de maior compatibilidade. Os cinco campos são exatamente os que o wizard
 * resolve por deep link, então servem para pré-selecionar a oferta na PDV.
 */
export interface FitScoreSelectedOffer {
  offerId: number
  // Id do tipo de turma — espelha ClassType.Enum (1 INDIVIDUAL, 2 GROUP, 3 INDIVIDUAL_GROUP,
  // 4 PLATFORM). É id, e não nome, porque o wizard casa tipo de turma por id
  classType: number
  // Populados apenas em produto presencial
  unitName: string | null
  state: string | null
  city: string | null
}

// Resposta crua da API
export interface FitScoreResponse {
  profileCompatibility: FitScoreLevel
  skillsCompatibility: FitScoreSkillsCompatibility
  // Só vem populado em LOW aprovado manualmente pelo CX. HIGH sempre null
  approved: boolean | null
  // Os três abaixo são ausentes na resposta legada (educação/formação antes da TS-9006)
  //
  // Categoria do cálculo — decide qual variação descreve a compatibilidade e quais skills o
  // painel de critérios explica
  category?: FitScoreCategory | null
  // Elegibilidade calculada pelo backend. Nenhuma tela reage a este campo — ver a doc de
  // FitScoreEligibilityStatus
  eligibilityStatus?: FitScoreEligibilityStatus | null
  // Oferta a que este cálculo se refere. Ausente sempre que o backend não identifica a oferta
  // avaliada, e é o que decide se o fit acompanha a seleção do wizard
  selectedOffer?: FitScoreSelectedOffer | null
}

// Formato compact usado em listas (search, recommended, summary) — só os campos
// essenciais pra UI/analytics sem carregar o skillsCompatibility completo.
export interface FitScoreSummary {
  level: FitScoreLevel
  approved: boolean | null
}

export interface ProductFitScoreResponse {
  fitScore: FitScoreResponse | null
}

// Skill já filtrada e enriquecida pelo formatter (vem em ordem canônica e só incompatíveis)
export interface FormattedFitScoreSkill {
  key: FitScoreSkillKey
  data: FitScoreSkillCompatibility
}

/**
 * Estrutura pronta para consumo na render — produzida pelo formatter no loader.
 *
 * `extends Required<FitScoreResponse>`: o contrato cru inteiro, com os opcionais resolvidos em
 * `null` pelo formatter, mais o que ele deriva. Herdar em vez de redeclarar não é só economia de
 * linhas — um campo novo na resposta aparece aqui automaticamente e o `tsc` cobra o formatter,
 * em vez de o campo existir no contrato e nunca chegar à tela.
 */
export interface FormattedFitScore extends Required<FitScoreResponse> {
  // Nível efetivo aplicado pela UI: HIGH sempre que approved=true, senão o original
  effectiveLevel: FitScoreLevel
  /**
   * Critérios que o backend avaliou nesta resposta, na ordem canônica — o que o painel "Como
   * calculamos sua compatibilidade?" explica, incluindo os compatíveis.
   *
   * Sai da presença da chave no payload, não da categoria: é o que faz o painel acertar sozinho
   * numa categoria que o front ainda não conhece.
   */
  evaluatedSkills: FitScoreSkillKey[]
  // Lista de skills com compatibility != HIGH (já filtrada e na ordem canônica)
  incompatibleSkills: FormattedFitScoreSkill[]
  // approved === true (boolean estrito pra usar em condicionais)
  isApproved: boolean
  /**
   * `effectiveLevel` é LOW ou MEDIUM: há algo a melhorar, e portanto recomendação a mostrar.
   *
   * Único booleano de nível, de propósito. `FitScoreLevel` tem três membros, então um segundo
   * booleano ("é HIGH") seria o complemento exato deste: não carregaria informação, só outra
   * forma de escrever a mesma pergunta — e de escrevê-la errado. Toda decisão de "tem o que
   * melhorar?" no fluxo lê este campo, incluindo os gates de perfil aprovado pelo CX.
   */
  showRecommendations: boolean
}

export const PRODUCT_FIT_SCORE_QUERY_KEY = 'product-fit-score'
